import React, { useMemo } from 'react';
import { Col, Row } from 'antd';
import { FormikProps } from 'formik';
import { Input } from 'formik-antd';

import FormItem from 'components/formik/FormItem';
import FormikSelectWithLoading from 'hocs/withLoading/FormikSelectWithLoading';
import { useAppSelector } from 'hooks/useAppSelector';
import {
  citiesSelector,
  countriesSelector,
  isCitiesLoadingSelector,
  isCountriesLoadingSelector,
  isStatesLoadingSelector,
  statesSelector,
} from 'redux/selectors/regionSelectors';
import FormikAutoCompleteWithLoading from 'hocs/withLoading/FormikAutoCompleteWithLoading';
import { CompanyFormValues } from '../CompanyModal';

type Props = {
  formik: FormikProps<CompanyFormValues>;
};
export default function CompanyStateSection(props: Props) {
  const { formik } = props;

  const countries = useAppSelector(countriesSelector);
  const isCountriesLoading = useAppSelector(isCountriesLoadingSelector);
  const states = useAppSelector(statesSelector);
  const isStatesLoading = useAppSelector(isStatesLoadingSelector);
  const cities = useAppSelector(citiesSelector);
  const isCitiesLoading = useAppSelector(isCitiesLoadingSelector);

  const filteredStates = useMemo(
    () => states.filter((state) => state.countryId === formik.values.country),
    [formik.values.country, states],
  );

  const filteredCompanyCities = useMemo(
    () => cities.filter((city) => city.stateId === formik.values.state),
    [formik.values.state, cities],
  );

  const handleCompanyStateChange = () => {
    formik.setFieldValue('city', '', false);
    formik.setFieldValue('internationalCityName', '', false);
  };

  const handleCountryChange = (
    countryId: number,
    formik: FormikProps<CompanyFormValues>,
  ) => {
    formik.setFieldValue('city', '', false);
    formik.setFieldValue('state', undefined, false);
    formik.setFieldValue('internationalCityName', '', false);
    formik.setFieldValue('internationalStateName', '', false);
  };

  return (
    <>
      <Row gutter={40}>
        <Col xs={24} md={12}>
          <Row gutter={20}>
            <Col xs={24} lg={12}>
              <FormItem name="country" label="Country *">
                <FormikSelectWithLoading
                  isLoading={isCountriesLoading}
                  name="country"
                  showSearch
                  placeholder="Select country"
                  optionFilterProp="children"
                  onChange={(value: number) =>
                    handleCountryChange(value, formik)
                  }
                >
                  {countries.map((country) => (
                    <FormikSelectWithLoading.Option
                      key={`select-country-${country.id}`}
                      value={country.id || -1}
                    >
                      {country.name}
                    </FormikSelectWithLoading.Option>
                  ))}
                </FormikSelectWithLoading>
              </FormItem>
            </Col>

            <Col xs={24} lg={12}>
              <FormItem name="state" label="State *">
                <FormikSelectWithLoading
                  isLoading={isStatesLoading}
                  name="state"
                  showSearch
                  disabled={!formik.values.country}
                  placeholder="Select state"
                  optionFilterProp="children"
                  onChange={() => handleCompanyStateChange()}
                >
                  {filteredStates.map((state) => (
                    <FormikSelectWithLoading.Option
                      key={`select-state-${state.id}`}
                      value={state.id || -1}
                    >
                      {state.name}
                    </FormikSelectWithLoading.Option>
                  ))}
                </FormikSelectWithLoading>
              </FormItem>
            </Col>

            <Col xs={24} lg={12}>
              <FormItem label="ZIP *" name="zip">
                <Input
                  name="zip"
                  placeholder="Enter ZIP code"
                  autoComplete="off"
                />
              </FormItem>
            </Col>
          </Row>
        </Col>

        <Col xs={24} md={12}>
          <Row gutter={20}>
            <Col xs={24} lg={12}>
              <FormItem name="city" label="City *">
                <FormikAutoCompleteWithLoading
                  isLoading={isCitiesLoading}
                  name="city"
                  filterOption
                  placeholder="Enter city"
                  showSearch
                  disabled={!formik.values.state}
                >
                  {filteredCompanyCities.map((city) => (
                    <FormikAutoCompleteWithLoading.Option
                      key={`company-city-${city.id}`}
                      value={city.name || ''}
                    >
                      {city.name}
                    </FormikAutoCompleteWithLoading.Option>
                  ))}
                </FormikAutoCompleteWithLoading>
              </FormItem>
            </Col>

            <Col xs={24} lg={12}>
              <FormItem label="Address *" name="address">
                <Input
                  name="address"
                  placeholder="Enter address"
                  autoComplete="off"
                />
              </FormItem>
            </Col>
          </Row>
        </Col>
      </Row>
    </>
  );
}
